import { CategoryNavigation, ClientNavigation } from "@edgetier/client-components";
import { SpinnerUntil } from "@edgetier/components";
import { IClient, IUser } from "@edgetier/watchtower-types";
import { faUserCog } from "@fortawesome/pro-solid-svg-icons";
import NavigationCategoryMenus from "components-for/navigation/navigation-categories/navigation-category-menus";
import { useUser } from "hooks-for/users/use-user";
import useUsersClients from "hooks-for/users/use-users-clients";
import logo from "images/icon-colour-light-background.svg";
import { FunctionComponent, memo } from "react";

import ClientDetails from "./client-details";
import ClientList from "./client-list";
import "./navigation.scss";
import { IProps } from "./navigation.types";
import SignOut from "./sign-out";
import { FeatureFlagToggler } from "../../internal-utilities/feature-flag-toggles/feature-flag-toggler";
import { useFlags } from "hooks-for/feature-flags";
import { FEATURE_FLAGS } from "types-for/feature-flags";

/**
 * Sidebar with links to other pages.
 * @param props.isAgentReviewEnabled
 * @param props.isExploreTagsEnabled
 */
const Navigation: FunctionComponent<IProps> = () => {
    const flags = useFlags([FEATURE_FLAGS.InternalFeatureFlagToggler]);
    const { data: user, isLoading: isUserLoading } = useUser();
    const { data: userClients, isLoading: isUserClientsLoading, isError: isUserClientsError } = useUsersClients();

    return (
        <ClientNavigation logo={logo}>
            <SpinnerUntil<{ user: IUser; userClients: IClient[] }>
                data={{ user, userClients }}
                isReady={!isUserLoading && !isUserClientsLoading}
            >
                {(data) => (
                    <>
                        <NavigationCategoryMenus />

                        <CategoryNavigation
                            icon={faUserCog}
                            label={`${user?.firstName} ${user?.surname}`}
                            placement="right-end"
                            className="user-details"
                        >
                            {Array.isArray(userClients) && userClients.length >= 2 && <ClientList />}
                            {flags[FEATURE_FLAGS.InternalFeatureFlagToggler].enabled && <FeatureFlagToggler />}
                            <SignOut />
                        </CategoryNavigation>

                        {isUserClientsError ? (
                            <li className="navigation-link navigation-link__error">
                                <div className="navigation-link__inner">Could not load client details</div>
                            </li>
                        ) : (
                            <>{data.userClients.length >= 2 && <ClientDetails clientId={user?.clientId!} />}</>
                        )}
                    </>
                )}
            </SpinnerUntil>
        </ClientNavigation>
    );
};

export default memo(Navigation);
